import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { API } from "@/lib/api";
import { TrustBadge } from "@/components/shared";
import { MotionButton, useMotionPresets, DUR, EASE } from "@/lib/motion";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2, XCircle, Eye, EyeOff, Volume2 } from "lucide-react";
import { EvidenceLightbox } from "@/components/proctor/EvidenceLightbox";
import { useSignedFiles } from "@/hooks/useSignedFiles";

const SEV_BADGE = { high: "flag", medium: "medium", low: "high" };
const AUDIO_TONE = {
  suspicious: "text-rose-300 bg-rose-500/10 border-rose-500/30",
  unclear: "text-muted-foreground bg-muted/40 border-border",
  benign: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
};
const AI_UI = {
  pending: ["AI checking…", "text-sky-300 bg-sky-500/10 border-sky-500/30"],
  confirmed: ["Confirmed", "text-amber-300 bg-amber-500/10 border-amber-500/30"],
  dismissed: ["AI dismissed", "text-muted-foreground bg-muted/40 border-border"],
  dismissed_by_reviewer: ["Dismissed by reviewer", "text-muted-foreground bg-muted/40 border-border"],
};
const isDismissed = (v) => (v.ai_status || "confirmed").startsWith("dismissed");

function DismissForm({ candId, v, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await api.post(`/candidates/${candId}/proctor/violations/${v.id}/dismiss`, { note }); toast.success("Marked as a false alarm"); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not dismiss"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 pt-1" onClick={(e) => e.stopPropagation()} data-testid={`dismiss-form-${v.id}`}>
      <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why is this a false alarm? (optional)" className="text-xs" data-testid="dismiss-note" />
      <MotionButton whileTap={{ scale: 0.97 }} size="sm" variant="outline" onClick={go} disabled={busy} data-testid="dismiss-confirm">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Dismiss as false alarm"}</MotionButton>
    </div>
  );
}

function FlagRow({ v, i, candId, signUrl, jump, canSeek, onChanged, reduce, openEvidence }) {
  const [open, setOpen] = useState(false);
  const [label, cls] = AI_UI[v.ai_status || "confirmed"] || AI_UI.confirmed;
  const dim = isDismissed(v);
  return (
    <motion.div layout
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
      animate={{ opacity: dim ? 0.55 : 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={reduce ? { duration: 0.001 } : { duration: DUR.base, ease: EASE.out, delay: Math.min(i, 8) * 0.04 }}
      role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); jump(v); } }} onClick={() => jump(v)}
      className={`p-3 rounded-lg border border-border bg-card space-y-2 ${canSeek(v) ? "cursor-pointer hover:border-primary/50 transition-colors" : ""}`} data-testid={`ci-violation-${i}`} data-ai-status={v.ai_status || "confirmed"}>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm font-medium ${dim ? "line-through decoration-muted-foreground/50" : ""}`}>{v.label || v.type.replace(/_/g, " ")}</span>
        <span className="flex items-center gap-1.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`} data-testid={`ai-status-${i}`}>{v.ai_status === "pending" && <Loader2 className="h-2.5 w-2.5 inline animate-spin mr-1" />}{label}</span>
          <TrustBadge type={SEV_BADGE[v.severity] || "medium"}>{v.severity}</TrustBadge>
        </span>
      </div>
      {v.ai_reason && <p className="text-xs text-primary/90 flex items-start gap-1.5" data-testid={`ai-reason-${i}`}><Sparkles className="h-3 w-3 shrink-0 mt-0.5" /> {v.ai_reason}</p>}
      {v.detail && <p className="text-xs text-muted-foreground">{v.detail}</p>}
      {v.review_note && <p className="text-xs text-muted-foreground italic">Reviewer note: {v.review_note}{v.reviewed_by ? ` — ${v.reviewed_by}` : ""}</p>}
      {v.audio_path && (
        <div className="space-y-1.5" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2">
            <Volume2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <audio controls preload="none" src={signUrl(v.audio_path)} className="h-8 w-full" data-testid={`evidence-audio-${i}`} />
          </div>
          {v.audio_assessment && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid={`audio-assessment-${i}`}>
              <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${AUDIO_TONE[v.audio_assessment.verdict] || AUDIO_TONE.unclear}`}><Sparkles className="h-2.5 w-2.5" /> Audio: {v.audio_assessment.verdict}</span>
              {v.audio_assessment.voice?.verdict === "other_voice" && <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${AUDIO_TONE.suspicious}`} data-testid={`voice-check-${i}`}>Voice: possible other voice</span>}
              <span className="text-muted-foreground">{v.audio_assessment.reason}</span>
              {v.audio_transcript ? <span className="w-full italic text-muted-foreground">“{v.audio_transcript}”</span> : null}
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-mono text-muted-foreground">{v.at ? new Date(v.at).toLocaleTimeString() : ""}</span>
        <span className="text-[10px] text-primary" data-testid={`moment-label-${i}`}>{canSeek(v) ? "Jump to moment →" : "No matching recording"}</span>
        {v.snapshot_path && <button onClick={(e) => { e.stopPropagation(); openEvidence(v); }} className="shrink-0 rounded border border-white/10 overflow-hidden hover:ring-2 hover:ring-primary/50 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-testid={`evidence-snapshot-${i}`} aria-label="Open evidence image"><img src={signUrl(v.snapshot_path)} alt="evidence frame" className="h-12 w-16 object-cover" /></button>}
      </div>
      {!dim && v.id && (open ? <DismissForm candId={candId} v={v} onDone={onChanged} /> : (
        <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid={`dismiss-btn-${i}`}><XCircle className="h-3 w-3" /> Dismiss as false alarm</button>
      ))}
    </motion.div>
  );
}

export function IntegrityTimeline({ candId, violations = [], token, jump, canSeek, onChanged }) {
  const [showDismissed, setShowDismissed] = useState(false);
  const [lbIndex, setLbIndex] = useState(null);
  const M = useMotionPresets();
  const signUrl = useSignedFiles(violations.flatMap((v) => [v.audio_path, v.snapshot_path]));
  const dismissed = all.filter(isDismissed).length;
  const rows = showDismissed ? all : all.filter((v) => !isDismissed(v));
  const evidence = rows.filter((v) => v.snapshot_path);
  const openEvidence = (v) => { const idx = evidence.findIndex((e) => (e.id || e) === (v.id || v)); setLbIndex(idx >= 0 ? idx : 0); };
  if (!all.length) return <p className="text-sm text-muted-foreground" data-testid="ci-proctor-empty">No proctoring flags. Clean session.</p>;
  return (
    <div className="space-y-2" data-testid="integrity-timeline">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Integrity timeline · {all.length - dismissed} active</span>
        {dismissed > 0 && (
          <button onClick={() => setShowDismissed((s) => !s)} className="text-xs text-primary inline-flex items-center gap-1" data-testid="toggle-dismissed">
            {showDismissed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {showDismissed ? "Hide" : "Show"} {dismissed} dismissed
          </button>
        )}
      </div>
      <AnimatePresence initial={false}>
        {rows.map((v, i) => <FlagRow key={v.id || i} v={v} i={i} candId={candId} signUrl={signUrl} jump={jump} canSeek={canSeek} onChanged={onChanged} reduce={M.reduce} openEvidence={openEvidence} />)}
      </AnimatePresence>
      {!rows.length && <p className="text-sm text-muted-foreground" data-testid="all-dismissed">Every flag in this session was dismissed as a false alarm.</p>}
      <EvidenceLightbox items={evidence} index={lbIndex} onIndexChange={setLbIndex} onClose={() => setLbIndex(null)} apiBase={API} token={token} />
    </div>
  );
}
